if (!customElements.get('localization-form')) {

  class LocalizationForm extends HTMLElement {

    connectedCallback() {

      if (this.controller) return;

      this.form = this.querySelector('form');

      if (!this.form) return;

      this.controller =
        new AbortController();

      const { signal } =
        this.controller;



      /*
       * ============================================
       * LANGUAGE / NATIVE COUNTRY SELECTS
       * ============================================
       */

      this.form
        .querySelectorAll(
          'select[name="locale_code"], select[name="country_code"]'
        )
        .forEach((select) => {

          if (
            select.hasAttribute('onchange')
          ) {
            return;
          }

          select.addEventListener(
            'change',
            () => {

              this.form.requestSubmit();

            },
            { signal }
          );

        });



      /*
       * ============================================
       * COUNTRY PICKERS
       * ============================================
       */

      this
        .querySelectorAll('.loc-country')
        .forEach((picker) => {

          this.setupCountryPicker(
            picker,
            signal
          );

        });

    }



    setupCountryPicker(
      picker,
      signal
    ) {

      const summary =
        picker.querySelector(
          '.loc-country__toggle'
        );

      const panel =
        picker.querySelector(
          '.loc-country__panel'
        );

      const searchInput =
        picker.querySelector(
          '.loc-country__search-input'
        );

      const empty =
        picker.querySelector(
          '.loc-country__empty'
        );

      const status =
        picker.querySelector(
          '.loc-country__status'
        );

      const items =
        Array.from(
          picker.querySelectorAll(
            '.loc-country__item'
          )
        );


      if (!summary || !panel) {
        return;
      }



      /*
       * ============================================
       * NORMALIZE SEARCH TEXT
       * ============================================
       */

      const normalize = (value = '') => {

        return value
          .normalize('NFD')
          .replace(
            /[\u0300-\u036f]/g,
            ''
          )
          .toLocaleLowerCase()
          .trim();

      };



      /*
       * ============================================
       * COUNTRY ENTRIES
       * ============================================
       */

      const entries =
        items.map((item) => {

          return {

            item,

            button:
              item.querySelector(
                '.loc-country__option'
              ),

            search:
              normalize(
                item.dataset.countrySearch ||
                ''
              )

          };

        });



      let query = '';

      let lastTypedAt = 0;



      /*
       * ============================================
       * GET VISIBLE BUTTONS
       * ============================================
       */

      const visibleButtons = () => {

        return entries
          .filter((entry) => {

            return (
              !entry.item.hidden &&
              entry.button
            );

          })
          .map((entry) => {

            return entry.button;

          });

      };



      /*
       * ============================================
       * RESET SEARCH
       * ============================================
       */

      const resetSearch = () => {

        query = '';

        lastTypedAt = 0;


        if (searchInput) {

          searchInput.value = '';

        }


        entries.forEach(
          ({ item }) => {

            item.hidden = false;

          }
        );


        if (empty) {

          empty.hidden = true;

        }


        if (status) {

          status.textContent = '';

        }

      };



      /*
       * ============================================
       * CLOSE PICKER
       * ============================================
       */

      const closePicker = (
        restoreFocus = false
      ) => {

        picker.open = false;

        resetSearch();


        if (restoreFocus) {

          summary.focus({
            preventScroll: true
          });

        }

      };



      /*
       * ============================================
       * FILTER COUNTRIES
       * ============================================
       */

      const filterCountries = (
        keepToggleFocus = false
      ) => {

        const normalizedQuery =
          normalize(query);


        const terms =
          normalizedQuery
            .split(/\s+/)
            .filter(Boolean);


        let count = 0;


        entries.forEach(
          ({ item, search }) => {

            const matches =
              terms.length === 0 ||

              terms.every(
                (term) =>
                  search.includes(term)
              );


            item.hidden =
              !matches;


            if (matches) {

              count += 1;

            }

          }
        );



        /*
         * Show empty state
         */

        if (empty) {

          empty.hidden =
            count !== 0;

        }



        /*
         * Accessibility message
         */

        if (status) {

          if (query.trim()) {

            status.textContent =
              `${count} countries match "${query.trim()}".`;

          } else {

            status.textContent =
              `${count} countries available.`;

          }

        }



        /*
         * Desktop keyboard search
         */

        if (keepToggleFocus) {

          summary.focus({
            preventScroll: true
          });


          panel.scrollTop = 0;

        }

      };



      /*
       * ============================================
       * MOBILE SEARCH INPUT
       * ============================================
       */

      if (searchInput) {

        /*
         * Search while typing
         */

        searchInput.addEventListener(
          'input',
          (event) => {

            query =
              event.target.value;

            lastTypedAt =
              Date.now();


            /*
             * false:
             * do not move focus away
             * from mobile input.
             */

            filterCountries(false);

          },
          { signal }
        );



        /*
         * Search keyboard
         */

        searchInput.addEventListener(
          'keydown',
          (event) => {

            /*
             * Stop parent keyboard
             * search from also running.
             */

            event.stopPropagation();



            /*
             * Escape behaviour
             */

            if (
              event.key === 'Escape'
            ) {

              event.preventDefault();


              if (
                searchInput.value
              ) {

                searchInput.value = '';

                query = '';

                filterCountries(false);


              } else {

                closePicker(true);

              }

            }



            /*
             * Enter selects
             * first visible result
             */

            if (
              event.key === 'Enter' &&
              searchInput.value.trim()
            ) {

              const firstButton =
                visibleButtons()[0];


              if (firstButton) {

                event.preventDefault();

                firstButton.click();

              }

            }



            /*
             * Arrow down moves into results
             */

            if (
              event.key === 'ArrowDown'
            ) {

              const buttons =
                visibleButtons();


              if (buttons.length) {

                event.preventDefault();

                buttons[0].focus({
                  preventScroll: true
                });

              }

            }

          },
          { signal }
        );



        /*
         * Prevent any outer click
         * logic being triggered.
         */

        searchInput.addEventListener(
          'click',
          (event) => {

            event.stopPropagation();

          },
          { signal }
        );

      }



      /*
       * ============================================
       * FOCUS BUTTON
       * ============================================
       */

      const focusButton = (
        button
      ) => {

        if (!button) return;


        button.focus({
          preventScroll: true
        });


        button.scrollIntoView({

          block: 'nearest',

          inline: 'nearest'

        });

      };



      /*
       * ============================================
       * DETAILS OPEN / CLOSE
       * ============================================
       */

      picker.addEventListener(
        'toggle',
        () => {

          /*
           * Closed
           */

          if (!picker.open) {

            resetSearch();

            return;

          }



          /*
           * Close other localization selectors
           */

          document
            .querySelectorAll(
              'localization-form .loc-country[open]'
            )
            .forEach((other) => {

              if (
                other !== picker
              ) {

                other.open = false;

              }

            });



          /*
           * Focus search automatically
           * ONLY on mobile.
           */

const mobile =
  window.matchMedia(
    '(max-width: 749px)'
  ).matches;

const isHeader =
  this.classList.contains('loc-ui--header');

const isFooter =
  this.classList.contains('loc-ui--footer');

/*
 * IMPORTANT:
 * Do NOT automatically focus the search input
 * inside the HEADER on mobile.
 *
 * iPhone Safari can zoom/reposition the entire
 * viewport when an input inside a sticky/fixed
 * header receives programmatic focus.
 *
 * Footer can keep the existing auto-focus.
 */
if (
  mobile &&
  searchInput &&
  !isHeader
) {

  requestAnimationFrame(() => {

    searchInput.focus({
      preventScroll: true
    });

  });

}

        },
        { signal }
      );



      /*
       * ============================================
       * CLICK OUTSIDE
       * ============================================
       */

      document.addEventListener(
        'click',
        (event) => {

          if (
            picker.open &&
            !picker.contains(
              event.target
            )
          ) {

            closePicker();

          }

        },
        { signal }
      );



      /*
       * ============================================
       * FOCUS LEAVES PICKER
       * ============================================
       */

      picker.addEventListener(
        'focusout',
        (event) => {

          /*
           * Focus still inside
           */

          if (
            event.relatedTarget &&
            picker.contains(
              event.relatedTarget
            )
          ) {

            return;

          }



          /*
           * Mobile Safari can sometimes
           * briefly return null.
           */

          setTimeout(
            () => {

              if (
                !picker.contains(
                  document.activeElement
                )
              ) {

                closePicker();

              }

            },
            0
          );

        },
        { signal }
      );



      /*
       * ============================================
       * DESKTOP KEYBOARD SEARCH
       * ============================================
       */

      picker.addEventListener(
        'keydown',
        (event) => {

          /*
           * Mobile search field handles
           * its own keyboard.
           */

          if (
            event.target ===
            searchInput
          ) {

            return;

          }



          /*
           * Ignore modifier keys
           */

          if (
            event.isComposing ||
            event.ctrlKey ||
            event.metaKey ||
            event.altKey
          ) {

            return;

          }



          const key =
            event.key;



          /*
           * ESC
           */

          if (
            key === 'Escape'
          ) {

            if (
              picker.open
            ) {

              event.preventDefault();

              event.stopPropagation();

              closePicker(true);

            }

            return;

          }



          /*
           * UP / DOWN
           */

          if (
            key === 'ArrowDown' ||
            key === 'ArrowUp'
          ) {

            event.preventDefault();

            picker.open = true;


            const buttons =
              visibleButtons();


            if (!buttons.length) {
              return;
            }


            const currentIndex =
              buttons.indexOf(
                document.activeElement
              );


            let nextIndex;


            if (
              key === 'ArrowDown'
            ) {

              nextIndex =
                currentIndex < 0

                  ? 0

                  : (
                      currentIndex + 1
                    ) %
                    buttons.length;


            } else {

              nextIndex =
                currentIndex <= 0

                  ? buttons.length - 1

                  : currentIndex - 1;

            }


            focusButton(
              buttons[nextIndex]
            );

            return;

          }



          /*
           * HOME / END
           */

          if (
            picker.open &&
            (
              key === 'Home' ||
              key === 'End'
            )
          ) {

            event.preventDefault();


            const buttons =
              visibleButtons();


            if (!buttons.length) {
              return;
            }


            focusButton(

              key === 'Home'

                ? buttons[0]

                : buttons[
                    buttons.length - 1
                  ]

            );

            return;

          }



          /*
           * ENTER selects first result
           * when using keyboard search.
           */

          if (
            key === 'Enter' &&
            picker.open &&
            query &&
            document.activeElement ===
              summary
          ) {

            event.preventDefault();


            const firstButton =
              visibleButtons()[0];


            if (firstButton) {

              firstButton.click();

            }

            return;

          }



          /*
           * BACKSPACE
           */

          if (
            key === 'Backspace' &&
            picker.open
          ) {

            event.preventDefault();


            query =
              Array
                .from(query)
                .slice(0, -1)
                .join('');


            lastTypedAt =
              Date.now();


            filterCountries(true);

            return;

          }



          /*
           * Ignore Tab, Shift,
           * CapsLock etc.
           */

          if (
            Array.from(key).length !== 1
          ) {

            return;

          }



          const now =
            Date.now();



          /*
           * Space should still open
           * the native details selector
           * when not actively searching.
           */

          if (
            key === ' ' &&
            (
              !query ||
              now - lastTypedAt > 1200
            )
          ) {

            return;

          }



          event.preventDefault();



          /*
           * Reset keyboard query
           * after inactivity.
           */

          if (
            now - lastTypedAt > 1200
          ) {

            query = '';

          }



          query += key;

          lastTypedAt = now;

          picker.open = true;


          filterCountries(true);

        },
        { signal }
      );

    }



    /*
     * ============================================
     * CLEANUP
     * ============================================
     */

    disconnectedCallback() {

      this.controller?.abort();

      this.controller = null;

    }

  }



  customElements.define(
    'localization-form',
    LocalizationForm
  );

}